import { useEffect, useId, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/shadcn/button';
import { Input } from '@/components/shadcn/input';
import { Checkbox } from '@/components/shadcn/checkbox';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/shadcn/form';
import { Alert, AlertDescription } from '@/components/shadcn/alert';
import { PasswordInput } from '@/components/auth/PasswordInput';
import { SocialLoginButtons } from '@/components/auth/SocialLoginButtons';
import {
  clearRememberedIdentifier,
  getRememberedIdentifier,
  setRememberedIdentifier,
} from '@/utils/rememberMe';

const loginSchema = z.object({
  identifier: z.string().min(1, 'Email atau username wajib diisi'),
  password: z.string().min(1, 'Password wajib diisi'),
});

type LoginFormValues = z.infer<typeof loginSchema>;

const RATE_LIMIT_SECONDS = 60;

export interface LoginFormProps {
  /** When true (login drawer), return to the page the user opened the drawer from. */
  stayOnCurrentPage?: boolean;
  onSuccess?: () => void;
  onForgotPassword?: () => void;
  forgotPasswordTo?: string;
}

export function LoginForm({
  stayOnCurrentPage = false,
  onSuccess,
  onForgotPassword,
  forgotPasswordTo = '/forgot-password',
}: LoginFormProps = {}) {
  const { login } = useAuth();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const returnUrl = stayOnCurrentPage
    ? `${location.pathname}${location.search}`
    : searchParams.get('returnUrl');
  const [apiError, setApiError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [rateLimitUntil, setRateLimitUntil] = useState<number | null>(null);
  const [rememberMe, setRememberMe] = useState(false);
  const rememberId = useId();

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { identifier: '', password: '' },
  });

  useEffect(() => {
    const saved = getRememberedIdentifier();
    if (saved) {
      form.setValue('identifier', saved);
      setRememberMe(true);
    }
  }, [form]);

  const isRateLimited = rateLimitUntil !== null && Date.now() < rateLimitUntil;

  const onSubmit = async (values: LoginFormValues) => {
    if (isRateLimited) return;
    setApiError(null);
    setIsSubmitting(true);
    try {
      if (rememberMe) {
        setRememberedIdentifier(values.identifier);
      } else {
        clearRememberedIdentifier();
      }
      await login(values, returnUrl);
      onSuccess?.();
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const message =
          (error.response?.data as { message?: string })?.message ??
          'Terjadi kesalahan sistem, silakan coba lagi nanti';
        setApiError(message);
        if (error.response?.status === 429) {
          setRateLimitUntil(Date.now() + RATE_LIMIT_SECONDS * 1000);
        }
      } else if (error instanceof Error) {
        setApiError(error.message);
      } else {
        setApiError('Terjadi kesalahan sistem, silakan coba lagi nanti');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      {apiError && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{apiError}</AlertDescription>
        </Alert>
      )}
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="identifier"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email atau Username</FormLabel>
                <FormControl>
                  <Input placeholder="email@contoh.com" autoComplete="username" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Password</FormLabel>
                <FormControl>
                  <PasswordInput
                    placeholder="Masukkan password"
                    autoComplete="current-password"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Checkbox
                id={rememberId}
                checked={rememberMe}
                onCheckedChange={(checked) => setRememberMe(checked === true)}
                aria-label="Ingat saya"
              />
              <label htmlFor={rememberId} className="text-sm text-muted-foreground">
                Ingat saya
              </label>
            </div>
            <Button variant="link" className="h-auto p-0 text-sm text-[#FC480D]" asChild>
              <Link to={forgotPasswordTo} onClick={() => onForgotPassword?.()}>
                Lupa Password?
              </Link>
            </Button>
          </div>
          <Button
            type="submit"
            className="w-full rounded-xl bg-[#FC480D] hover:bg-[#e3400b]"
            disabled={isSubmitting || isRateLimited}
          >
            {isSubmitting ? 'Memproses...' : isRateLimited ? 'Coba lagi nanti' : 'Masuk'}
          </Button>
        </form>
      </Form>
      <SocialLoginButtons
        disabled={isSubmitting || isRateLimited}
        stayOnCurrentPage={stayOnCurrentPage}
        onSuccess={onSuccess}
      />
    </div>
  );
}
